import React from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { COMMUNICATION_TYPE_NAME, POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';
import { addressToString } from '../../../common/utils/address';
import { useUserContext } from '../../../common/utils/context';
import { getFullMachineName } from '../../../common/utils/machine';
import getPOSModelName from '../../../common/utils/machine/getPOSModelName';
import { valueExists } from '../../../common/utils/utils';
import Text from '../../components/text/Text';
import { Card, ListRow, SectionTitle, Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getMachineDirectionsParams } from '../docket/docketDetails/DocketLocationDetail';
import MachineCommunication from '../machine/MachineCommunication';
import MachineTemperature from '../machine/MachineTemperature';
import { MapLink } from '../map/MapLink';

const pluralNotes = count => (count === 1 ? 'note' : 'notes');

// Important notes stay in full; the rest open from one row.
const NotesRows = ({ importantNotes, otherCount, canCreateNotes, noun, onPress }) => {
	const styles = useThemedStyles(createStyles);
	const hasImportant = Boolean(importantNotes?.length);

	return (
		<>
			{hasImportant && importantNotes.map(note => (
				<Card key={note.id} tone="warning" style={styles.importantNote}>
					<Tag label="Important" tone="danger" />
					<Text style={styles.noteText}>{note.description}</Text>
				</Card>
			))}
			{(otherCount > 0 || canCreateNotes) && (
				<ListRow
					icon="file-text"
					title={otherCount > 0
						? `${otherCount} ${hasImportant ? 'other ' : ''}${noun} ${pluralNotes(otherCount)}`
						: `Add ${noun} note`}
					onPress={onPress}
				/>
			)}
		</>
	);
};

/**
 * Machine and location details of a stop (the stop's "Machine" page), in the
 * design system: status tags and value lines, then rows for notes, previous
 * photos, directions and the full detail pages. Same data, links and
 * permissions as the classic DocketPOSDetail / DocketLocationDetail blocks.
 * Props: docket (with machine, place and location).
 */
const StopMachineDetails = ({ docket }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();
	const canSeeMachineDetail = hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_DETAIL_PAGE);
	const canCreateNotes = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_NOTES);

	const { machine } = docket;
	const { location } = machine;
	const isMarket = machine.type === POS_TYPE.MARKET;
	const isOffline = !machine.commDevices?.length;
	const machineName = getFullMachineName(machine, machine.place, location);
	const posModelName = getPOSModelName(machine);
	const posNoun = posModelName.toLowerCase();
	// Unknown values (newer server enums) fall back to their raw key, as on the classic screen.
	const receivedCommunicationTypeNames = (machine.receivedCommunicationTypes || [])
		.filter(Boolean)
		.map(type => COMMUNICATION_TYPE_NAME[type] || type);
	const otherMachineNotes = machine.noteCount - (machine.importantNotes?.length || 0);
	const otherLocationNotes = location ? location.noteCount - (location.importantNotes?.length || 0) : 0;
	const mapLocation = location
		? { ...location, coordinates: location.coordinates?.length ? location.coordinates : machine.coordinates }
		: (machine.coordinates?.length ? { coordinates: machine.coordinates } : null);
	const showAddress = Boolean(mapLocation?.coordinates?.length || addressToString(mapLocation?.addressObj));

	const openMachineDetail = (extraParams = {}) => navigation.push('MachineDetailTabView', {
		docketId: docket.id,
		machineId: machine.id,
		machineName,
		locationId: location?.id,
		isMarket,
		...extraParams,
	});

	return (
		<>
			<Card style={styles.card}>
				<SectionTitle title={`${posModelName} details`} />
				<View style={styles.tags}>
					<Tag label={isOffline ? `Offline ${posNoun}` : 'Online'} tone={isOffline ? 'warning' : 'success'} size="regular" />
					{Boolean(machine.wrap?.name) && <Tag label={`Wrap: ${machine.wrap.name}`} size="regular" />}
				</View>
				{!isOffline && (
					<View style={styles.lines}>
						<Text style={styles.line}>
							{`${isMarket ? 'Kiosk(s)' : 'Communication device'}: ${machine.commDevices.map(commDevice => commDevice.deviceId).join(', ')}`}
						</Text>
						<Text style={styles.line}>
							{receivedCommunicationTypeNames.length
								? `Received communication type(s): ${receivedCommunicationTypeNames.join(' | ')}`
								: `${posModelName} doesn't receive communication`}
						</Text>
						<View style={styles.valueRow}>
							<Text style={styles.line}>Last communication</Text>
							<MachineCommunication communication={machine.lastCommunication} />
						</View>
						{valueExists(machine.temperatureCelcius) && (
							<View style={styles.valueRow}>
								<Text style={styles.line}>Last reported temperature</Text>
								<MachineTemperature temperature={machine.temperatureCelcius} />
							</View>
						)}
					</View>
				)}
			</Card>
			<View style={styles.rows}>
				{(Boolean(machine.noteCount) || canCreateNotes) && (
					<NotesRows
						importantNotes={machine.importantNotes}
						otherCount={otherMachineNotes}
						canCreateNotes={canCreateNotes}
						noun={posNoun}
						onPress={() => navigation.push('MachineNotes', { machineId: machine.id, docketId: docket.id })}
					/>
				)}
				<ListRow
					icon="camera"
					title="Previous photos"
					onPress={() => navigation.push('MachinePhotoHistoryView', { machineId: machine.id, machineName, excludeDocketId: docket.id, docketId: docket.id })}
				/>
				{canSeeMachineDetail && (
					<ListRow icon="more-horizontal" title={`All ${posNoun} details`} onPress={() => openMachineDetail()} />
				)}
			</View>

			<View style={styles.rows}>
				<SectionTitle title="Location details" />
				{showAddress && <MapLink location={mapLocation} />}
				<ListRow
					icon="navigation"
					title="Directions"
					onPress={() => navigation.push('MachineDirectionsView', { ...getMachineDirectionsParams(machine, canSeeMachineDetail), docketId: docket.id })}
				/>
				{Boolean(location) && (Boolean(location.noteCount) || canCreateNotes) && (
					<NotesRows
						importantNotes={location.importantNotes}
						otherCount={otherLocationNotes}
						canCreateNotes={canCreateNotes}
						noun="location"
						onPress={() => navigation.push('LocationNotes', { locationId: location.id, docketId: docket.id })}
					/>
				)}
				{!location && <Text style={styles.line}>No location assigned</Text>}
				{canSeeMachineDetail && Boolean(location) && (
					<ListRow icon="more-horizontal" title="All location details" onPress={() => openMachineDetail({ locationTab: true })} />
				)}
			</View>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	card: {
		gap: space.md,
	},
	tags: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	lines: {
		gap: space.sm,
	},
	line: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	valueRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	rows: {
		gap: space.sm,
	},
	importantNote: {
		gap: space.sm,
		alignItems: 'flex-start',
	},
	noteText: {
		...type.bodySmall,
		color: colors.text,
	},
});

export default StopMachineDetails;
